import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PaymentElement } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import {
  Box,
  Stack,
  Typography,
  Alert,
  AlertTitle,
  Backdrop,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import directClient from "../../api-clients/directClient";
import endpoints from "../../api-clients/endpoints";
import { useUserStateContext } from "App";
import DirectShippingDetails from "pages/directCheckout/DirectShippingDetails";
import DirectOrderSummary from "./DirectOrderSummary";
import DirectCarts from "./DirectCarts";
import CheckoutLoadingProgress from "custom/loading/CheckoutLoadingProgress";
import FreightOptimizer from "./freightOptimizer/FreightOptimizer";

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_API_KEY,
);

const StyledInput = ({ poNumber, setPoNumber, inputError, setFormErrors }) => {
  const [isPOFieldFocused, setIsPOFieldFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const baseStyles = {
    boxSizing: "border-box",
    borderWidth: "thin",
    outlineWidth: "thin",
    borderRadius: "10px",
    padding: "8px",
    fontSize: "16px",
    transition: "all 0.2s ease-in-out",
  };

  const getBorderStyle = (isPOFieldFocused, inputError) => {
    if (inputError) return "1px solid #df1b41"; // same as stripe dangerRed
    if (isPOFieldFocused) return "1px solid rgb(2, 129, 192)";
    return "1px solid #c8c8c8";
  };

  const getOutlineStyle = (isHovered, isPOFieldFocused, inputError) => {
    if (inputError) return "1px solid #df1b41"; // same as stripe dangerRed
    if (isHovered) return "1px solid #150D0A";
    if (isPOFieldFocused) return "1px solid rgb(2, 129, 192)";
    return "none";
  };

  const getBackgroundColor = (poNumber) => {
    return poNumber.length > 0 ? "rgba(221, 241, 247, 0.9)" : "transparent";
  };

  const getBoxShadow = (isPOFieldFocused) => {
    return isPOFieldFocused ? "0 0 0 1px rgb(2, 129, 192)" : "none";
  };

  const computedStyles = {
    ...baseStyles,
    border: getBorderStyle(isPOFieldFocused, inputError),
    backgroundColor: getBackgroundColor(poNumber),
    outline: getOutlineStyle(isHovered, isPOFieldFocused, inputError),
    boxShadow: getBoxShadow(isHovered, isPOFieldFocused),
  };

  const handleChange = (e) => {
    setPoNumber(e.target.value);
    if (inputError) {
      setFormErrors((prevFormErrors) => {
        return {
          ...prevFormErrors,
          poNumber: false,
        };
      });
    }
  };

  return (
    <Box className="w-fit">
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        gap="10px"
        sx={{
          width: "fit-content",
          p: 1,
        }}
      >
        <label htmlFor="poNumber" className="text-wolfGrey text-lg">
          PO Number:
        </label>
        <input
          autoComplete="off"
          type="text"
          id="poNumber"
          value={poNumber}
          onFocus={() => setIsPOFieldFocused(true)}
          onBlur={() => setIsPOFieldFocused(false)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onChange={handleChange}
          style={computedStyles}
        />
      </Stack>
      {inputError && (
        <Box className="flex flex-row justify-end mr-[10px] mb-[6px]">
          <p className="text-[#df1b41] text-sm m-[0px]">
            Your PO number is incomplete.
          </p>
        </Box>
      )}
    </Box>
  );
};

export function CheckoutForm({
  userCreds,
  checkoutDetails,
  fetchCheckoutDetails,
  loadingCheckoutDetails,
  selectedAddress,
  setSelectedAddress,
  selectedShippingMethods,
  setSelectedShippingMethods,
  poNumber,
  setPoNumber,
  formErrors,
  setFormErrors,
  setShippingTotal,
  needAddress,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  selectedPaymentMethodID,
  setSelectedPaymentMethodID,
}) {
  if (needAddress) {
    return (
      <DirectShippingDetails
        userCreds={userCreds}
        checkoutDetails={checkoutDetails}
        setSelectedShippingMethods={setSelectedShippingMethods}
        fetchCheckoutDetails={fetchCheckoutDetails}
        selectedAddress={selectedAddress}
        setSelectedAddress={setSelectedAddress}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        setShippingTotal={setShippingTotal}
        hasAddress={false}
        loadingCheckoutDetails={loadingCheckoutDetails}
      />
    );
  }
  return (
    <Box
      className="grow flex flex-col gap-[42px]"
      sx={{
        "& > *": {
          border: "1px solid lightgray",
          borderLeft: "2px solid rgb(2, 129, 192)", // lighterblueblue
          backgroundColor: "#fff",
        },
      }}
    >
      <StyledInput
        poNumber={poNumber}
        setPoNumber={setPoNumber}
        inputError={formErrors.poNumber}
        setFormErrors={setFormErrors}
      />
      <DirectShippingDetails
        userCreds={userCreds}
        checkoutDetails={checkoutDetails}
        setSelectedShippingMethods={setSelectedShippingMethods}
        fetchCheckoutDetails={fetchCheckoutDetails}
        selectedAddress={selectedAddress}
        setSelectedAddress={setSelectedAddress}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        setShippingTotal={setShippingTotal}
      />
      <RenderPaymentDetails
        userCreds={userCreds}
        checkoutDetails={checkoutDetails}
        selectedShippingMethods={selectedShippingMethods}
        loadingCheckoutDetails={loadingCheckoutDetails}
        selectedAddress={selectedAddress}
        poNumber={poNumber}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        selectedPaymentMethodID={selectedPaymentMethodID}
        setSelectedPaymentMethodID={setSelectedPaymentMethodID}
      />
      <DirectCarts vendors={checkoutDetails?.vendors} />
    </Box>
  );
}

function RenderPaymentDetails({
  userCreds,
  checkoutDetails,
  selectedShippingMethods,
  loadingCheckoutDetails,
  selectedAddress,
  poNumber,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  selectedPaymentMethodID,
  setSelectedPaymentMethodID,
}) {
  if (loadingCheckoutDetails || !checkoutDetails?.total) {
    return;
  }

  // Check if we have vendor-specific payment options
  const hasVendors =
    checkoutDetails?.vendors && checkoutDetails.vendors.length > 0;
  const vendorPaymentOptions = hasVendors
    ? checkoutDetails.vendors[0]?.paymentOptions
    : null;
  const paymentMethods = vendorPaymentOptions?.payment_methods || [];
  const useVendorPayments = paymentMethods.length > 0;

  // Set initial payment method if needed
  React.useEffect(() => {
    if (
      useVendorPayments &&
      paymentMethods.length > 0 &&
      !selectedPaymentMethod
    ) {
      setSelectedPaymentMethod(paymentMethods[0]);
      setSelectedPaymentMethodID(paymentMethods[0].id); // Set the ID when setting initial payment method
    }
  }, [
    paymentMethods,
    useVendorPayments,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    setSelectedPaymentMethodID,
  ]);

  // Handle payment method selection change
  const handlePaymentMethodChange = (event) => {
    const selectedId = event.target.value;
    const selectedMethod = paymentMethods.find(
      (method) => method.id === selectedId,
    );
    setSelectedPaymentMethod(selectedMethod);
    setSelectedPaymentMethodID(selectedId); // Store the ID separately
  };

  const paymentElementOptions = {
    layout: "tabs",
  };

  return (
    <Box>
      <Box
        sx={{
          p: 0.75,
          px: 2,
          mb: "28px",
        }}
        className="flex items-center bg-grayscaleIce"
      >
        <Typography
          variant="h5"
          gutterBottom
          className="text-wolfGrey mb-[0px]"
        >
          Billing Details
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        {useVendorPayments ? (
          <Box>
            <Typography variant="h6" className="text-charcoal mb-3">
              Select Payment Method
            </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedPaymentMethod?.id || ""}
                onChange={handlePaymentMethodChange}
                sx={{
                  "& .MuiOutlinedInput-input": {
                    paddingTop: "15.5px",
                    paddingBottom: "8.5px",
                  },
                }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select a payment method
                </MenuItem>
                {paymentMethods.map((method) => (
                  <MenuItem key={method.id} value={method.id}>
                    {method.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {vendorPaymentOptions?.external_required && (
              <Box className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
                <Typography className="text-xs text-gray-500 mt-[8px] ml-[8px]">
                  This vendor requires payment through their external system.
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <PaymentElement
            id="payment-element"
            options={paymentElementOptions}
          />
        )}
      </Box>
    </Box>
  );
}

async function getDirectCheckoutDetails(
  userCreds,
  setCustomerSessionClientSecret,
  setCheckoutDetails,
  setLoadingCheckoutDetails,
  address,
  setSelectedAddress,
  setSelectedShippingMethods,
  loadingDescriptionIntervalRef,
  navigate,
  setShippingTotal,
  setError,
  vendorCode,
  setFreightOptimizerOpen,
) {
  if (!userCreds?.cognitoID) {
    return;
  }

  setLoadingCheckoutDetails(true);
  const headers = {
    "User-Identifier": userCreds.cognitoID,
  };

  const params = {};
  try {
    let checkoutDetailsResp;
    if (address?.street1) {
      const body = {
        action: "shippingAddress",
        shippingAddress: address,
      };
      checkoutDetailsResp = await directClient.post(
        endpoints.vendorAgnosticShippingAddress(vendorCode),
        body,
        {
          headers,
          params,
        },
      );
    } else {
      checkoutDetailsResp = await directClient.get(
        endpoints.vendorAgnosticCheckout(vendorCode),
        {
          headers,
          params,
        },
      );
    }
    setFreightOptimizerOpen(
      checkoutDetailsResp.data?.vendors?.find(
        (vendor) => vendor.directVendor === vendorCode,
      )?.freightOptimizerRequired,
    );
    setCheckoutDetails(checkoutDetailsResp.data);
    setShippingTotal(checkoutDetailsResp.data?.shippingTotal?.toFixed(2));
    setCustomerSessionClientSecret(
      checkoutDetailsResp.data?.customerSessionClientSecret,
    );
    setSelectedAddress(checkoutDetailsResp.data?.shippingAddresses?.[0]);
    setSelectedShippingMethods(() => {
      const initialMethods = [];
      if (checkoutDetailsResp.data?.vendors) {
        checkoutDetailsResp.data.vendors.forEach((vendor) => {
          if (vendor.shippingMethods && vendor.shippingMethods.length > 0) {
            const method = vendor.shippingMethods[0];
            initialMethods.push({
              selectedShippingMethod: method,
              selectedShippingMethodID: method.id,
              directVendor: vendor?.directVendor,
              vendorName: vendor?.vendorName,
              vendorAddress: vendor?.vendorAddress,
            });
          }
        });
      }
      return initialMethods;
    });
    setLoadingCheckoutDetails(false);
  } catch (error) {
    console.error(
      `Error getting checkout details for direct: ${error?.response?.data?.errorMessage}`,
    );
    console.log(error.response);
    setError(
      error?.response?.data?.errorMessage ||
        "An error occurred while loading checkout details",
    );
    if (error?.response?.status === 400) {
      // ValueError -> no items in cart
      navigate("/cart");
      return;
    }

    setLoadingCheckoutDetails(false);
  } finally {
    clearInterval(loadingDescriptionIntervalRef.current);
  }
}

const pageLoadingMessages = [
  "Loading your addresses and billing methods...",
  "Almost done...",
];

export const PageLoadingBackdrop = ({ loading }) => {
  return (
    <Backdrop
      sx={{
        backgroundColor: "rgba(88, 88, 88, 0.2)",
        zIndex: (theme) => theme.zIndex.modal,
      }}
      open={loading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  );
};

export default function DirectCheckoutPage({}) {
  // Add a validation step for supported distributors
  const SUPPORTED_DISTRIBUTORS = ["direct", "wsp", "ss"];

  const [customerSessionClientSecret, setCustomerSessionClientSecret] =
    useState("");
  const [checkoutDetails, setCheckoutDetails] = useState({});
  const [loadingCheckoutDetails, setLoadingCheckoutDetails] = useState(true);
  const [selectedAddress, setSelectedAddress] = useState(
    checkoutDetails?.shippingAddresses?.[0] || {},
  );
  const [poNumber, setPoNumber] = useState(
    new Date().toLocaleDateString("en-US"),
  );
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedPaymentMethodID, setSelectedPaymentMethodID] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const vendorCode = useParams().vendorCode;

  // Validate the vendor is supported
  useEffect(() => {
    if (!SUPPORTED_DISTRIBUTORS.includes(vendorCode)) {
      setError(`Unsupported vendor: ${vendorCode}`);
      navigate("/cart"); // Redirect to cart if distributor is not supported
    }
  }, [vendorCode, navigate]);

  const [selectedShippingMethods, setSelectedShippingMethods] = useState(() => {
    const initialMethods = [];
    if (checkoutDetails.vendors) {
      checkoutDetails.vendors.forEach((vendor) => {
        if (vendor.shippingMethods && vendor.shippingMethods.length > 0) {
          const method = vendor.shippingMethods[0];
          initialMethods.push({
            selectedShippingMethod: method,
            selectedShippingMethodID: method.id,
            directVendor: vendor?.directVendor,
            vendorName: vendor?.vendorName,
            vendorAddress: vendor?.vendorAddress,
          });
        }
      });
    }
    return initialMethods;
  });
  const [formErrors, setFormErrors] = useState({});
  const { userCreds } = useUserStateContext();
  const [loadingMessagesIndex, setLoadingMessagesIndex] = useState(0);
  const loadingDescriptionIntervalRef = useRef();
  const [shippingTotal, setShippingTotal] = useState(0);
  const [freightOptimizerOpen, setFreightOptimizerOpen] = useState(false);

  const handleCloseFreightOptimizer = () => {
    setFreightOptimizerOpen(false);
  };

  const fetchCheckoutDetails = async (address) => {
    await getDirectCheckoutDetails(
      userCreds,
      setCustomerSessionClientSecret,
      setCheckoutDetails,
      setLoadingCheckoutDetails,
      address,
      setSelectedAddress,
      setSelectedShippingMethods,
      loadingDescriptionIntervalRef,
      navigate,
      setShippingTotal,
      setError,
      vendorCode,
      setFreightOptimizerOpen
    );
  };
  useEffect(() => {
    fetchCheckoutDetails(selectedAddress);
  }, []);

  useEffect(() => {
    loadingDescriptionIntervalRef.current = setInterval(() => {
      setLoadingMessagesIndex((prevIndex) =>
        prevIndex < pageLoadingMessages.length - 1 ? prevIndex + 1 : prevIndex,
      );
    }, 4000);

    return () => clearInterval(loadingDescriptionIntervalRef.current);
  }, []);

  // if there are already checkoutDetails to display, we want to use the Backdrop
  if (loadingCheckoutDetails && Object.keys(checkoutDetails).length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CheckoutLoadingProgress
          message={pageLoadingMessages[loadingMessagesIndex]}
        />
      </div>
    );
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ fontSize: "15px " }}>
        <AlertTitle sx={{ fontSize: "18px " }}>{error}</AlertTitle>
      </Alert>
    );
  }
  const needAddress = !checkoutDetails?.shippingAddresses?.length;
  if (needAddress) {
    return (
      <Box className="w-full h-[100vh] flex flex justify-center pt-[48px]">
        <Box className="flex flex-col items-center gap-[36px] max-w-[800px]">
          <Box className="flex flex-col gap-[12px]">
            <Typography className="text-wolfGrey text-2xl text-center">
              Please add a shipping address before continuing to checkout.
            </Typography>
            <Typography className="text-wolfGrey text-xl text-center">
              You will only ever have to complete this step once.
            </Typography>
          </Box>

          <CheckoutForm
            userCreds={userCreds}
            checkoutDetails={checkoutDetails}
            loadingCheckoutDetails={loadingCheckoutDetails}
            fetchCheckoutDetails={fetchCheckoutDetails}
            setCheckoutDetails={setCheckoutDetails}
            selectedAddress={selectedAddress}
            setSelectedAddress={setSelectedAddress}
            selectedShippingMethods={selectedShippingMethods}
            setSelectedShippingMethods={setSelectedShippingMethods}
            poNumber={poNumber}
            setPoNumber={setPoNumber}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            setShippingTotal={setShippingTotal}
            needAddress={needAddress}
            selectedPaymentMethodID={selectedPaymentMethodID}
            setSelectedPaymentMethodID={setSelectedPaymentMethodID}
          />
        </Box>
      </Box>
    );
  }

  const options = {
    amount: Math.round((checkoutDetails?.total || 0) * 100),
    mode: "payment",
    currency: "usd",
    paymentMethodCreation: "manual",
    setup_future_usage: "off_session",
    appearance: {
      theme: "flat",
    },
  };
  // Check if we have vendor-specific payment options
  const hasVendors =
    checkoutDetails?.vendors && checkoutDetails.vendors.length > 0;
  const vendorPaymentOptions = hasVendors
    ? checkoutDetails.vendors[0]?.paymentOptions
    : null;
  const paymentMethods = vendorPaymentOptions?.payment_methods || [];
  const useVendorPayments = paymentMethods.length > 0;

  const formInputs = {
    poNumber: poNumber.length > 0,
    shippingAddress: Object.keys(selectedAddress).length > 0,
  };

  // Add payment method validation if using vendor payments
  if (useVendorPayments) {
    formInputs.paymentMethod = selectedPaymentMethod !== null;
  }
  for (const vendor of checkoutDetails?.vendors) {
    const key = "shippingMethod" + vendor.directVendor;
    const vendorShippingMethod = selectedShippingMethods.find(
      (method) => method.directVendor === vendor.directVendor,
    );
    if (vendorShippingMethod) {
      formInputs[key] =
        Object.keys(vendorShippingMethod?.selectedShippingMethod)?.length > 0;
    } else {
      formInputs[key] = false;
    }
  }

  return (
    <Elements options={options} stripe={stripePromise}>
      <Box
        className="flex flex-col items-center lg:items-start lg:flex-row gap-[36px]"
        sx={{ my: 4, mx: 1 }}
      >
        <CheckoutForm
          userCreds={userCreds}
          checkoutDetails={checkoutDetails}
          loadingCheckoutDetails={loadingCheckoutDetails}
          fetchCheckoutDetails={fetchCheckoutDetails}
          setCheckoutDetails={setCheckoutDetails}
          selectedAddress={selectedAddress}
          setSelectedAddress={setSelectedAddress}
          selectedShippingMethods={selectedShippingMethods}
          setSelectedShippingMethods={setSelectedShippingMethods}
          poNumber={poNumber}
          setPoNumber={setPoNumber}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
          setShippingTotal={setShippingTotal}
          selectedPaymentMethod={selectedPaymentMethod}
          setSelectedPaymentMethod={setSelectedPaymentMethod}
          selectedPaymentMethodID={selectedPaymentMethodID}
          setSelectedPaymentMethodID={setSelectedPaymentMethodID}
        />
        <DirectOrderSummary
          userCreds={userCreds}
          checkoutDetails={checkoutDetails}
          selectedAddress={selectedAddress}
          poNumber={poNumber}
          selectedShippingMethods={selectedShippingMethods}
          formInputs={formInputs}
          setFormErrors={setFormErrors}
          shippingTotal={shippingTotal}
          needAddress={needAddress}
          selectedPaymentMethod={selectedPaymentMethod} // Pass selected payment method
          selectedPaymentMethodID={selectedPaymentMethodID} // Pass selected payment method ID
        />
        <PageLoadingBackdrop loading={loadingCheckoutDetails} />
        <FreightOptimizer
          open={freightOptimizerOpen}
          onClose={handleCloseFreightOptimizer}
          freightOptions={
            checkoutDetails?.vendors?.find(
              (vendor) => vendor.directVendor === vendorCode,
            )?.freightOptimizerOptions || []
          }
          setCheckoutDetails={setCheckoutDetails}
          checkoutDetails={checkoutDetails}
          setShippingTotal={setShippingTotal}
        />
      </Box>
    </Elements>
  );
}
